import { apiClient } from './api'

// WhiteLabel Types
export interface WhiteLabelConfig {
  id: string
  merchant: string
  merchant_name: string
  custom_domain?: string
  logo?: string
  favicon?: string
  primary_color: string
  secondary_color: string
  company_display_name?: string
  support_email?: string
  support_phone?: string
  page_title?: string
  meta_description?: string
  enable_custom_checkout: boolean
  enable_dashboard_access: boolean
  enable_api_access: boolean
  enable_branding_css: boolean
  created_at: string
  updated_at: string
}

export interface WhiteLabelConfigCreateData {
  merchant: string
  custom_domain?: string
  logo?: File
  favicon?: File
  primary_color?: string
  secondary_color?: string
  company_display_name?: string
  support_email?: string
  support_phone?: string
  page_title?: string
  meta_description?: string
  enable_custom_checkout?: boolean
  enable_dashboard_access?: boolean
  enable_api_access?: boolean
  enable_branding_css?: boolean
}

export interface WhiteLabelConfigUpdateData {
  custom_domain?: string
  logo?: File
  favicon?: File
  primary_color?: string
  secondary_color?: string
  company_display_name?: string
  support_email?: string
  support_phone?: string
  page_title?: string
  meta_description?: string
  enable_custom_checkout?: boolean
  enable_dashboard_access?: boolean
  enable_api_access?: boolean
  enable_branding_css?: boolean
}

// CNAME Domain Types (NEW)
export interface WhiteLabelDomain {
  id: string
  merchant: string | number
  merchant_name: string
  domain: string
  is_verified: boolean
  is_active: boolean
  is_primary: boolean
  verification_code?: string
  verified_at?: string
  ssl_enabled: boolean
  ssl_cert_path?: string
  full_url: string
  created_at: string
  updated_at: string
}

export interface WhiteLabelDomainCreateData {
  merchant: string | number
  domain: string
  is_active?: boolean
  is_primary?: boolean
  ssl_enabled?: boolean
  ssl_cert_path?: string
}

export interface WhiteLabelDomainUpdateData {
  domain?: string
  is_active?: boolean
  is_primary?: boolean
  ssl_enabled?: boolean
  ssl_cert_path?: string
}

export interface DomainVerificationRequest {
  verification_method: 'dns' | 'email'
}

export interface DomainVerificationResponse {
  domain: string
  is_verified: boolean
  verified_at?: string
  message: string
  instructions?: {
    record_type: string
    record_name: string
    record_value: string
    ttl: number
    verification_code?: string
  }
}

export interface MerchantDomainInfo {
  merchant: string
  merchant_name: string
  current_domain: string
  primary_domain?: WhiteLabelDomain
  all_domains: WhiteLabelDomain[]
  branding: WhiteLabelConfig
}

/**
 * WhiteLabel Service
 * Handles white-label configuration and CNAME domain operations
 */
export const whiteLabelService = {
  /**
   * Get all white-label configurations
   */
  async getAllConfigs(params?: {
    page?: number
    page_size?: number
    merchant?: string
    ordering?: string
  }): Promise<{
    count: number
    next: string | null
    previous: string | null
    results: WhiteLabelConfig[]
  }> {
    return apiClient.get('/core/white-label-configs/', { params })
  },

  /**
   * Get white-label config by ID
   */
  async getConfigById(id: string): Promise<WhiteLabelConfig> {
    return apiClient.get(`/core/white-label-configs/${id}/`)
  },

  /**
   * Get white-label config by merchant ID
   */
  async getConfigByMerchant(merchantId: string): Promise<WhiteLabelConfig | null> {
    try {
      const configs = await apiClient.get<{
        count: number
        next: string | null
        previous: string | null
        results: WhiteLabelConfig[]
      }>('/core/white-label-configs/', {
        params: { merchant: merchantId }
      })
      return configs.results?.[0] || null
    } catch (error) {
      return null
    }
  },

  /**
   * Create new white-label configuration
   */
  async createConfig(data: WhiteLabelConfigCreateData): Promise<WhiteLabelConfig> {
    const formData = new FormData()
    
    // Append all fields to FormData
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value.toString())
        } else {
          formData.append(key, value as string)
        }
      }
    })

    return apiClient.post<WhiteLabelConfig>('/core/white-label-configs/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  /**
   * Update white-label configuration
   */
  async updateConfig(id: string, data: WhiteLabelConfigUpdateData): Promise<WhiteLabelConfig> {
    const formData = new FormData()
    
    // Append all fields to FormData
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        if (value instanceof File) {
          formData.append(key, value)
        } else if (typeof value === 'boolean') {
          formData.append(key, value.toString())
        } else {
          formData.append(key, value as string)
        }
      }
    })

    return apiClient.patch<WhiteLabelConfig>(`/core/white-label-configs/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },  

  /**
   * Delete white-label configuration
   */
  async deleteConfig(id: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{ success: boolean; message: string }>(`/core/white-label-configs/${id}/`)
    return response
  },

  // ===== NEW CNAME DOMAIN METHODS =====

  /**
   * Get all domains for the current user
   */
  async getAllDomains(params?: {
    page?: number
    limit?: number
  }): Promise<{
    results: WhiteLabelDomain[]
    count: number
    next?: string | null
    previous?: string | null
  }> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('page_size', params.limit.toString())

    return apiClient.get('/white-label-domains/', { params })
  },

  /**
   * Get domain by ID
   */
  async getDomainById(id: string): Promise<WhiteLabelDomain> {
    return apiClient.get(`/white-label-domains/${id}/`)
  },

  /**
   * Get all domains for current merchant
   */
  async getMerchantDomains(merchantId?: string | number): Promise<WhiteLabelDomain[]> {
    return apiClient.get<WhiteLabelDomain[]>(`/white-label-domains/`, {
      params: merchantId ? { merchant_id: merchantId } : undefined
    })
  },

  /**
   * Get merchant's primary domain
   */
  async getMerchantPrimaryDomain(merchantId: string | number): Promise<WhiteLabelDomain | null> {
    const domains = await this.getMerchantDomains(merchantId)
    return domains.find((d) => d.is_primary) || null
  },

  /**
   * Get merchant info and domains by domain name (for white-label)
   */
  async getMerchantInfoByDomain(domain: string): Promise<MerchantDomainInfo | null> {
    try {
      const response = await apiClient.get<MerchantDomainInfo | null>(`/white-label-domains/get-by-domain/`, {
        params: { domain }
      })
      // Backend returns null (not 404) when domain is not found, which is handled gracefully
      if (!response || response === null) {
        console.log(`No merchant found for domain: ${domain}`)
        return null
      }
      return response
    } catch (error) {
      console.log(`No merchant found for domain: ${domain}`, error)
      return null
    }
  },

  /**
   * Create new domain
   */
  async createDomain(data: WhiteLabelDomainCreateData): Promise<WhiteLabelDomain> {
    return apiClient.post<WhiteLabelDomain>('/white-label-domains/', data)
  },

  /**
   * Update domain
   */
  async updateDomain(id: string, data: Partial<WhiteLabelDomainUpdateData>): Promise<WhiteLabelDomain> {
    return apiClient.patch<WhiteLabelDomain>(`/white-label-domains/${id}/`, data)
  },

  /**
   * Delete domain
   */
  async deleteDomain(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(`/white-label-domains/${id}/`)
  },

  /**
   * Generate verification code
   */
  async generateVerificationCode(
    domainId: string
  ): Promise<{
    domain: string
    verification_code: string
    instructions: {
      record_type: string
      record_name: string
      record_value: string
      ttl: number
      verification_code: string
    }
  }> {
    return apiClient.post(`/white-label-domains/${domainId}/generate-verification-code/`, {})
  },

  /**
   * Verify domain via DNS
   */
  async verifyDomain(domainId: string, method: 'dns' | 'email' = 'dns'): Promise<DomainVerificationResponse> {
    return apiClient.post(`/white-label-domains/${domainId}/verify/`, {
      verification_method: method
    })
  },

  /**
   * Set domain as primary
   */
  async setDomainAsPrimary(domainId: string): Promise<WhiteLabelDomain> {
    return apiClient.post(`/white-label-domains/${domainId}/set-primary/`, {})
  },

  /**
   * Toggle domain active status
   */
  async toggleDomainActive(domainId: string, isActive: boolean): Promise<WhiteLabelDomain> {
    return apiClient.post(`/white-label-domains/${domainId}/toggle-active/`, {
      is_active: isActive
    })
  },

  /**
   * Get current domain from browser
   */
  getCurrentDomain(): string {
    if (typeof window === 'undefined') return ''
    return window.location.hostname
  },

  /**
   * ===== ADMIN ONLY METHODS =====
   */

  /**
   * Get all domains for a merchant (admin only)
   */
  async getMerchantDomainsAdmin(merchantId: string | number): Promise<{
    merchant: { id: string | number; name: string }
    domains: WhiteLabelDomain[]
    count: number
  }> {
    const queryParams = new URLSearchParams()
    queryParams.append('merchant_id', merchantId.toString())

    const url = `/white-label-domains/by_merchant/?${queryParams.toString()}`
    return apiClient.get(url)
  },

  /**
   * Create domain for a merchant as admin
   */
  async createDomainAsAdmin(merchantId: string | number, domain: string, options?: {
    is_active?: boolean
    ssl_enabled?: boolean
  }): Promise<WhiteLabelDomain> {
    return apiClient.post(`/white-label-domains/`, {
      merchant: merchantId,
      domain,
      is_active: options?.is_active ?? true,
      ssl_enabled: options?.ssl_enabled ?? true
    })
  },

  /**
   * Update domain as admin
   */
  async updateDomainAsAdmin(
    domainId: string,
    data: Partial<WhiteLabelDomainUpdateData>
  ): Promise<WhiteLabelDomain> {
    return apiClient.put(`/white-label-domains/${domainId}/`, data)
  },

  /**
   * Delete domain as admin
   */
  async deleteDomainAsAdmin(domainId: string): Promise<void> {
    return apiClient.delete(`/white-label-domains/${domainId}/`)
  },

  /**
   * Verify domain as admin
   */
  async verifyDomainAsAdmin(domainId: string): Promise<DomainVerificationResponse> {
    return apiClient.post(`/white-label-domains/${domainId}/verify/`, {
      verification_method: 'dns'
    })
  },

  /**
   * Set primary domain as admin
   */
  async setPrimaryDomainAsAdmin(domainId: string): Promise<WhiteLabelDomain> {
    return apiClient.post(`/white-label-domains/${domainId}/set-primary/`, {})
  },

  /**
   * Toggle domain status as admin
   */
  async toggleDomainStatusAsAdmin(domainId: string, isActive: boolean): Promise<WhiteLabelDomain> {
    return apiClient.post(`/white-label-domains/${domainId}/toggle-active/`, {
      is_active: isActive
    })
  },

  /**
   * ===== MERCHANT API METHODS =====
   */

  /**
   * Get list of all merchants (admin only)
   */
  async getAllMerchants(params?: {
    search?: string
    page?: number
    limit?: number
    status?: string
  }): Promise<{
    results: Array<{
      id: string | number
      name: string
      company_name: string
      email: string
      status: string
      is_verified: boolean
      users_count: number
    }>
    count: number
    next?: string | null
    previous?: string | null
  }> {
    const queryParams = new URLSearchParams()
    if (params?.search) queryParams.append('search', params.search)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('page_size', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)

    const url = `/admin/merchants/?${queryParams.toString()}`
    return apiClient.get(url)
  },

  /**
   * Get merchant by ID (admin only)
   */
  async getMerchantById(merchantId: string | number): Promise<{
    id: string | number
    name: string
    company_name: string
    email: string
    status: string
    is_verified: boolean
    users_count: number
    currency: string
    created_at: string
  }> {
    return apiClient.get(`/admin/merchants/${merchantId}/`)
  },

  /**
   * Search merchants by name/email
   */
  async searchMerchants(query: string): Promise<Array<{
    id: string | number
    name: string
    company_name: string
    email: string
    status: string
  }>> {
    const result = await this.getAllMerchants({ search: query, limit: 20 })
    return result.results
  }
}